import React, { useEffect, useState } from "react";
import { useNavigate, NavLink } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import challenge_invite from "/src/assets/challenge-invite.png";
import { Share2, SendHorizontal, Copy } from "lucide-react";
import { toast } from "react-toastify";
import ChallengeLog from "../pages/ChallengeLog";

function PracticeUI() {
  const navigate = useNavigate();
  const [userIdInput, setUserIdInput] = useState("");

  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [subtopics, setSubtopics] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedTopic, setSelectedTopic] = useState("");
  const [selectedSubtopic, setSelectedSubtopic] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [inviteLink, setInviteLink] = useState("");
  const level = "medium";
  const [opponentHandle, setOpponentHandle] = useState("");
  const [friendList, setFriendList] = useState([]);
  const [isLoadingFriends, setIsLoadingFriends] = useState(true);
  const [isPreparingChallenge, setIsPreparingChallenge] = useState(false);
  const [opponentId, setOpponentId] = useState("");
  // New state for number of questions
  const [numberOfQuestions, setNumberOfQuestions] = useState(5);
  const userId = localStorage.getItem("userId");
  const baseUrl = import.meta.env.VITE_APP_BASE_URL;
  // Question options array
  const questionOptions = [5, 10, 15];

  // New state for progressive loading
  const [isUpperPartLoaded, setIsUpperPartLoaded] = useState(false);
  const [isLowerPartLoaded, setIsLowerPartLoaded] = useState(false);

  const inviteByUserId = async () => {
    if (!userIdInput) {
      toast.warn("Please enter a friend's User ID.");
      return;
    }

    console.log(
      "Adding friend with user_id:",
      userId,
      "friend_username:",
      userIdInput
    );

    try {
      const res = await axios.post(`${baseUrl}/v2/user/add-friend`, {
        user_id: userId,
        friend_username: userIdInput,
      });

      console.log("Add friend response:", res.data); // Debug log
      console.log("Response status:", res.status); // Debug log

      // Check if the request was successful (status 200 or 201)
      if (res.status === 200 || res.status === 201) {
        setOpponentId(res.data.friend_id || res.data.friendId);
        setOpponentHandle(userIdInput);
        closeModal();

        // Show green success toast
        toast.success(res.data.message || "Friend added successfully!", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          toastId: "friend-added-success", // Prevent duplicate toasts
          style: {
            backgroundColor: "#86EFAC", // Light green color
            color: "#166534", // Dark green text for contrast
            fontWeight: "500",
          },
        });

        // Refresh the friend list with a small delay to ensure backend has processed
        setTimeout(async () => {
          try {
            console.log("Refreshing friend list..."); // Debug log
            const refreshRes = await axios.get(
              `${baseUrl}/v2/user/${userId}/friends`
            );
            console.log("Refreshed friends:", refreshRes.data); // Debug log
            setFriendList(refreshRes.data.friends || []);
          } catch (refreshError) {
            console.error("Failed to refresh friends:", refreshError);
          }
        }, 1000);
      } else {
        console.log("API returned non-success status:", res.status); // Debug log
        toast.error(res.data.message || "Failed to add friend.");
      }
    } catch (err) {
      console.error("Add friend error:", err);
      console.error("Error response:", err.response?.data); // Debug log
      const errorMessage =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Could not add friend. Please check the User ID or try again.";
      toast.error(errorMessage);
    }
  };

  const inviteByLink = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${baseUrl}/play/challenges`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          creatorId: userId,
          subject: selectedSubject,
          topic: selectedTopic,
          level,
          subtopic: selectedSubtopic,
          mode: "async",
        }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || "Failed to create challenge link.");
      }

      const data = await res.json();
      const fullInviteLink = `${window.location.origin}/challenge/join/${data.inviteCode}`;
      setInviteLink(fullInviteLink);
      navigate(`/challenge/waiting/${data.challengeId}`);
    } catch (error) {
      toast.error(`Failed to generate challenge link: ${error.message}`);
      console.error(error);
    }
  };

  useEffect(() => {
    const fetchFriends = async () => {
      setIsLoadingFriends(true);
      try {
        const res = await axios.get(`${baseUrl}/v2/user/${userId}/friends`);
        setFriendList(res.data.friends);
      } catch (error) {
        console.error("Failed to fetch friends:", error);
      } finally {
        setIsLoadingFriends(false);
      }
    };
    fetchFriends();
  }, [baseUrl, userId]);

  // Effect to mark upper part as loaded when subjects and friends are ready
  useEffect(() => {
    if (subjects.length > 0 && !isLoadingFriends) {
      setIsUpperPartLoaded(true);
    }
  }, [subjects, isLoadingFriends]);

  // Effect to load lower part after upper part is loaded
  useEffect(() => {
    if (isUpperPartLoaded && !isLowerPartLoaded) {
      // Add a small delay to show progressive loading
      const timer = setTimeout(() => {
        setIsLowerPartLoaded(true);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [isUpperPartLoaded, isLowerPartLoaded]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      toast.success("Link copied to clipboard!");
    } catch (err) {
      toast.error("Failed to copy link");
    }
  };

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => {
    setIsModalOpen(false);
    setUserIdInput("");
    // setInviteLink(''); // This variable doesn't seem to be used or defined for invite links
  };

  useEffect(() => {
    async function fetchSubjects() {
      const res = await fetch(`${baseUrl}/api/subjects`);
      const data = await res.json();

      setSubjects(data);

      // Auto-select the first subject if available
      if (data && data.length > 0) {
        setSelectedSubject(data[0]);
      }
    }
    fetchSubjects();
  }, [baseUrl]); // Added baseUrl to dependency array

  useEffect(() => {
    if (selectedSubject) {
      async function fetchTopics() {
        const res = await fetch(
          `${baseUrl}/api/subjects/${selectedSubject}/topics`
        );
        const data = await res.json();
        console.log(data);
        setTopics(data);

        // Auto-select the first topic if available
        if (data && data.length > 0) {
          setSelectedTopic(data[0].name);
        }
      }
      fetchTopics();
    }
  }, [selectedSubject, baseUrl]); // Added baseUrl to dependency array

  useEffect(() => {
    if (selectedTopic) {
      async function fetchSubtopics() {
        const res = await fetch(
          `${baseUrl}/api/subjects/${selectedSubject}/topics/${selectedTopic}/subtopics`
        );
        const data = await res.json();
        setSubtopics(data);
      }
      fetchSubtopics();
    }
  }, [selectedTopic, selectedSubject, baseUrl]); // Added selectedSubject and baseUrl to dependency array

  // const handleChallengeFriendClick = async () => {

  //   if (selectedSubject && selectedTopic) {

  //     navigate(`playwithfriends-Challenge?subject=${selectedSubject}&topic=${selectedTopic}&subtopic=${selectedSubtopic}`);
  //   } else {
  //     toast.warn("Please select a subject and topic to challenge a friend.");
  //   }
  // };

  const handlePlayNowClick = async () => {
    try {
      setIsPreparingChallenge(true);
      console.log(opponentId);
      const token = localStorage.getItem("token");
      const res = await fetch(`${baseUrl}/play/challenges`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          creatorId: userId,
          opponentId: opponentId,
          subject: selectedSubject,
          topic: selectedTopic,
          level: "medium",
          subtopic: selectedSubtopic || null,
          numberOfQuestions: numberOfQuestions,
          mode: "sync",
        }),
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(
          error.detail || error.message || "Failed to create challenge"
        );
      }

      const data = await res.json();
      navigate(`/challenge/waiting/${data.challengeId}`);
      toast.success(data.message || "Challenge created successfully!");
    } catch (error) {
      console.error("Error starting challenge:", error);
      toast.error(error.message || "Could not start challenge.");
    } finally {
      setIsPreparingChallenge(false);
    }
  };

  return (
    <div className="bg-white rounded-lg p-4 font-sans">
      {/* Upper Part - Subject/Chapter/Friends */}
      {!isUpperPartLoaded ? (
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mb-4"></div>
            <p className="text-gray-600 font-medium">
              Loading subjects and friends...
            </p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-start">
          <div className="flex flex-col">
            <label className="block mb-1.5 text-gray-700 font-bold">
              Select a Subject
            </label>
            {/* Modified Select Container for visual consistency with right section */}
            <div className="bg-white py-2 rounded-lg shadow-sm border border-gray-200 mb-5">
              <div className="flex items-center p-2.5 bg-transparent">
                {" "}
                {/* bg-transparent to show parent background */}
                <img
                  src="/assets/images/maths.png"
                  alt="Mathematics Icon"
                  className="w-5 h-5 mr-2.5"
                />
                <select
                  className="border-none outline-none flex-grow text-base text-gray-700 pr-5 bg-transparent appearance-none" // appearance-none to remove default arrow
                  value={selectedSubject}
                  onChange={(e) => setSelectedSubject(e.target.value)}
                >
                  <option value="" disabled hidden>
                    Select a Subject
                  </option>
                  {subjects.map((subject, idx) => (
                    <option key={idx} value={subject}>
                      {subject}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <label className="block mb-1.5 text-gray-700 font-bold">
              Select a Chapter
            </label>
            {/* Modified Select Container for visual consistency */}
            <div className="bg-white py-2 rounded-lg shadow-sm border border-gray-200 mb-5">
              <div className="flex items-center p-2.5 bg-transparent">
                <img
                  src="/assets/images/maths.png"
                  alt="Algebra Icon"
                  className="w-5 h-5 mr-2.5"
                />
                <select
                  className="border-none outline-none flex-grow text-base text-gray-700 pr-5 bg-transparent appearance-none" // appearance-none
                  value={selectedTopic}
                  onChange={(e) => setSelectedTopic(e.target.value)}
                >
                  <option value="" disabled hidden>
                    Select a Chapter
                  </option>
                  {selectedSubject &&
                    topics.map((topic, idx) => (
                      <option key={idx} value={topic.name}>
                        {topic.name}
                      </option>
                    ))}
                </select>
              </div>
            </div>

            {subtopics.length > 0 && (
              <>
                <label className="block mb-1.5 text-gray-700 font-bold">
                  Select a Topic (Optional)
                </label>
                {/* Modified Select Container for visual consistency */}
                <div className="bg-white py-2 rounded-lg shadow-sm border border-gray-200 mb-5">
                  <div className="flex items-center p-2.5 bg-transparent">
                    <select
                      className="border-none outline-none flex-grow text-base text-gray-700 pr-5 bg-transparent appearance-none" // appearance-none
                      value={selectedSubtopic}
                      onChange={(e) => setSelectedSubtopic(e.target.value)}
                    >
                      <option value="">All Topic</option>
                      {subtopics.map((subtopic, idx) => (
                        <option key={idx} value={subtopic.name}>
                          {subtopic.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </>
            )}
            {/* Number of Questions Selector - Only show when subject and topic are selected */}
            {selectedSubject && selectedTopic && (
              <>
                <div className="border border-gray-300 rounded-md overflow-hidden mb-5 px-4 py-2 bg-white">
                  <div className="flex items-center">
                    <div className="block mb-1.5 text-gray-700 font-bold mr-4">
                      Select Number of Questions:
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {questionOptions.map((num) => (
                        <button
                          key={num}
                          onClick={() => setNumberOfQuestions(num)}
                          className={`w-10 h-10 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-xs sm:text-sm font-medium transition-colors ${
                            numberOfQuestions === num
                              ? "bg-[#0D99FF] text-white"
                              : "bg-gray-200 text-gray-800"
                          }`}
                        >
                          {num}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Right Section: Friend List / Selected Friend */}
          <div className="h-[400px] border-gray-700 border-opacity-50 rounded-lg flex flex-col items-center p-4 bg-[#F2F0FF82] shadow-md border border-gray-200">
            {" "}
            {/* Added padding and consistent styling */}
            {opponentId ? (
              <div className="flex flex-col items-center justify-center h-full space-y-2 p-4">
                <img
                  src={
                    friendList.find((f) => f.id === opponentId)?.profile ||
                    "/assets/images/You.png"
                  }
                  alt="Selected Friend"
                  className="w-16 h-16 rounded-full object-cover border-2 border-purple-500" // Increased size, added border
                />
                <p className="text-lg font-semibold text-gray-800">
                  {" "}
                  {/* Increased text size */}
                  {(() => {
                    const selectedFriend = friendList.find(
                      (f) => f.id === opponentId
                    );
                    const name =
                      selectedFriend?.fullName ||
                      opponentHandle ||
                      "Unknown User";
                    return name
                      ? name.charAt(0).toUpperCase() +
                          name.slice(1).toLowerCase()
                      : "";
                  })()}
                </p>
                <p className="text-sm text-green-600">Friend Selected</p>
              </div>
            ) : (
              <div className="h-full w-full px-2 py-3 flex flex-col gap-3 justify-between">
                <h3 className="self-center text-xl font-semibold">
                  {friendList.length} Friends
                </h3>
                {/* Search Friends Input - Added consistent styling */}
                <div className="relative flex items-center mb-2">
                  <input
                    type="text"
                    placeholder="Search friends"
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-purple-500 text-sm"
                  />
                  <svg
                    className="absolute left-3 text-gray-400 h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    ></path>
                  </svg>
                </div>

                {isLoadingFriends ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="flex flex-col items-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mb-3"></div>
                      <p className="text-gray-500">Loading friends...</p>
                    </div>
                  </div>
                ) : friendList.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-sm text-gray-500 text-center">
                      No friends yet. Add new friends!
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col overflow-y-auto space-y-2">
                    {friendList.map((friend) => (
                      <div
                        onClick={() => {
                          setOpponentHandle(friend.handle);

                          setOpponentId(friend.id);
                        }}
                        key={friend.id}
                        className="flex items-center space-x-3 bg-white p-2 rounded-lg shadow-sm hover:bg-gray-100 transition cursor-pointer border border-gray-200" // Added consistent border and shadow
                      >
                        <img
                          src={friend.profile || "/assets/images/You.png"}
                          alt={friend.fullName}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                        <span className="text-sm font-medium text-gray-800">
                          {friend.fullName
                            ? friend.fullName.charAt(0).toUpperCase() +
                              friend.fullName.slice(1).toLowerCase()
                            : ""}
                        </span>
                        <span className="ml-auto text-gray-400 text-sm">
                          ...
                        </span>{" "}
                        {/* Added ellipsis for consistency */}
                      </div>
                    ))}
                  </div>
                )}
                {/* Moved the button below the friend list scroll area */}
                <button
                  className="bg-[#0D99FF] text-white rounded-3xl border-none cursor-pointer font-bold py-2.5 px-4 text-md transition-colors duration-200 hover:bg-purple-700 block mt-auto" // mt-auto pushes it to bottom
                  onClick={openModal}
                >
                  + Invite New Friends
                </button>
              </div>
            )}
          </div>
          <AnimatePresence>
            {isModalOpen && (
              <>
                <motion.div
                  className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 0.5 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  onClick={closeModal}
                />
                <motion.div
                  className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white p-4 sm:p-5 rounded-lg shadow-lg w-[90%] max-w-md text-center z-[1001]"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.2 }}
                >
                  <h2 className="text-purple-700 text-base sm:text-lg font-bold mb-4 sm:mb-5">
                    Add New Friend
                  </h2>
                  <div className="flex flex-col sm:flex-row items-center gap-3 rounded-xl border border-gray-300 p-3 mb-4">
                    <img
                      src={challenge_invite}
                      alt="Challenge Invite"
                      className="w-12 h-auto"
                    />
                    <div className="text-left flex-1">
                      <div className="font-bold text-gray-800 text-xs sm:text-base mb-1">
                        Challenge Friend Via Link
                      </div>
                      <p className="text-gray-600 text-xs sm:text-sm">
                        Use the link to add your friend to play
                      </p>
                    </div>
                    <button
                      className="bg-white text-black border-none cursor-pointer flex items-center justify-center p-2 rounded-full hover:bg-gray-100 transition-colors duration-200"
                      onClick={inviteByLink}
                    >
                      <Share2 size={18} />
                    </button>
                  </div>
                  {inviteLink && (
                    <div className="flex flex-col sm:flex-row items-center bg-gray-100 rounded-md px-3 py-2 mb-4">
                      <input
                        type="text"
                        value={inviteLink}
                        readOnly
                        className="flex-1 bg-transparent border-none outline-none text-gray-800 text-xs sm:text-sm mb-2 sm:mb-0"
                      />
                      <button
                        className="bg-purple-600 text-white p-2 rounded-md hover:bg-purple-700 transition-colors duration-200"
                        onClick={copyToClipboard}
                      >
                        <Copy size={16} />
                      </button>
                    </div>
                  )}
                  <div className="text-gray-700 font-semibold my-3 sm:my-4 text-xs sm:text-sm">
                    or
                  </div>
                  <div className="font-bold text-gray-800 text-xs sm:text-base mb-2 sm:mb-3 text-left">
                    Add Friend by their User ID
                  </div>
                  <div className="flex flex-col sm:flex-row items-center gap-2 mb-4">
                    <input
                      type="text"
                      placeholder="Enter Friends User ID"
                      className="p-2 border border-gray-300 rounded-lg flex-1 text-xs sm:text-sm focus:ring-purple-500 focus:border-purple-500"
                      value={userIdInput}
                      onChange={(e) => setUserIdInput(e.target.value)}
                    />
                    <button
                      className="bg-purple-600 text-white p-2 rounded-lg hover:bg-purple-700 transition-colors duration-200"
                      onClick={inviteByUserId}
                    >
                      <SendHorizontal size={18} />
                    </button>
                  </div>
                  <button
                    className="bg-white text-gray-700 border border-gray-300 py-2 px-4 rounded-full cursor-pointer mt-4 hover:bg-gray-100 transition-colors duration-200 text-xs sm:text-sm"
                    onClick={closeModal}
                  >
                    Cancel
                  </button>
                </motion.div>
              </>
            )}
          </AnimatePresence>
          {/* Play Now / Challenge Friend Buttons */}
          {
            <div className="md:col-span-2 p-4 flex justify-center sm:justify-between items-center gap-4">
              {" "}
              {/* Centered on mobile, spread on sm+ */}
              <button
                className="bg-[#0D99FF] text-white border-none py-2.5 px-4 rounded-3xl cursor-pointer hover:bg-purple-700 transition-colors duration-200 text-base sm:text-lg w-full sm:w-auto disabled:opacity-75 disabled:cursor-not-allowed"
                onClick={handlePlayNowClick}
                disabled={isPreparingChallenge}
              >
                {isPreparingChallenge ? "Preparing..." : "Play Now"}
              </button>
              {/* <button className="bg-white border border-gray-500 text-gray-700 py-2.5 px-4 rounded-3xl cursor-pointer hover:bg-gray-100 transition-colors duration-200 text-base sm:text-lg w-full sm:w-auto" onClick={handleChallengeFriendClick}>
                  Challenge Your Friend!
              </button> */}
            </div>
          }
        </div>
      )}

      {/* Lower Part - Challenge Log */}
      {isUpperPartLoaded && (
        <div className="mt-6">
          {!isLowerPartLoaded ? (
            <div className="flex items-center justify-center h-32 bg-gray-50 rounded-lg">
              <div className="flex flex-col items-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mb-2"></div>
                <p className="text-gray-600 text-sm">
                  Loading challenge history...
                </p>
              </div>
            </div>
          ) : (
            <ChallengeLog />
          )}
        </div>
      )}
    </div>
  );
}

export default PracticeUI;
